import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Lock, CheckCircle } from "@phosphor-icons/react";
import api, { formatApiError, errContext } from "../lib/api";
import { useAuth } from "../lib/auth";

function MissingTokenView() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 bg-grid px-4 py-8">
      <div className="w-full max-w-md bg-white border border-zinc-950 p-8" data-testid="activate-no-token">
        <div className="font-mono text-[10px] tracking-[0.2em] text-[#D0021B] mb-2">/ MISSING_TOKEN</div>
        <h2 className="font-display text-3xl font-black tracking-tight">Activation link required.</h2>
        <p className="text-sm text-zinc-600 mt-3">Please use the activation link from the approval email.</p>
        <Link to="/login" className="mt-6 inline-block text-zinc-950 underline underline-offset-4 hover:text-[#FF3E00]">← Back to sign in</Link>
      </div>
    </div>
  );
}

function PasswordField({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="label-sm text-zinc-500">{label}</label>
      <div className="flex items-center gap-3 border-b border-zinc-900/20 focus-within:border-zinc-950 py-2">
        <Lock size={16} className="text-zinc-400" />
        <input
          data-testid={testId}
          type="password"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required minLength={8}
          className="flex-1 bg-transparent outline-none text-sm"
        />
      </div>
    </div>
  );
}

export default function ActivatePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const { logout } = useAuth();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const validate = () => {
    if (password !== confirm) return "Passwords do not match.";
    if (password.length < 8) return "Password must be at least 8 characters.";
    return "";
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const v = validate();
    if (v) { setError(v); return; }
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/public/access-request/activate", { token, password });
      // Bug fix: if the activator was already signed in as another account
      // (e.g. the superuser who approved the request clicked their own email),
      // that session cookie would let /login auto-redirect them back into
      // the OLD account. Call logout() unconditionally — it's a no-op on the
      // server if there's no session, and guarantees a fresh slate even if the
      // AuthProvider hasn't hydrated `user` yet at this point.
      try { await logout(); } catch { /* server may already be signed out — proceed */ }
      toast.success("Account activated — please sign in with your new password.");
      const email = data?.email ? encodeURIComponent(data.email) : "";
      navigate(`/login${email ? `?email=${email}` : ""}`, { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail, errContext(err)) || err.message);
    } finally { setLoading(false); }
  };

  if (!token) return <MissingTokenView />;

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 bg-grid px-4 py-8">
      <div className="w-full max-w-md bg-white border border-zinc-950 p-8" data-testid="activate-page">
        <div className="w-14 h-14 bg-[#FF3E00]/10 border border-[#FF3E00] flex items-center justify-center mb-4">
          <CheckCircle size={28} weight="fill" className="text-[#FF3E00]" />
        </div>
        <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-500 mb-2">/ ACTIVATE_ACCOUNT</div>
        <h2 className="font-display text-3xl font-black tracking-tight">Set your password.</h2>
        <p className="text-sm text-zinc-500 mt-1">Choose a password to activate your SmartERP account.</p>

        <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="activate-form">
          <PasswordField label="New password (min 8)" value={password} onChange={setPassword} testId="activate-password-input" />
          <PasswordField label="Confirm password" value={confirm} onChange={setConfirm} testId="activate-confirm-input" />

          {error && (
            <div className="border border-[#D0021B] bg-red-50 text-[#D0021B] text-xs px-3 py-2 font-mono" data-testid="activate-error">
              {error}
            </div>
          )}

          <button
            data-testid="activate-submit-btn"
            type="submit"
            disabled={loading}
            className="w-full bg-zinc-950 text-white font-semibold text-sm py-3 hover:bg-[#FF3E00] disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
          >
            {loading ? "ACTIVATING…" : "ACTIVATE ACCOUNT"}
            {!loading && <ArrowRight size={14} weight="bold" />}
          </button>
        </form>
      </div>
    </div>
  );
}
